import { useEffect, useRef } from 'react'
import type { Spotlight } from '../../hooks/useSpotlight'
import { useTranslation } from '../../hooks/useTranslation'
import type { CartItem } from '../../types/display'
import CartItemRow from './CartItemRow'
import { EASE_OUT, play } from '../../design/motion'

export default function CartList({
  items,
  currency,
  spotlight = null,
}: {
  items: CartItem[]
  currency: string
  spotlight?: Spotlight | null
}) {
  const { t } = useTranslation()
  // Defensive: RTDB can hand us a sparse/keyed object instead of an array
  // (e.g. after a mid-cart item deletion) even though it's typed as one —
  // never let that reach `.map` and unmount the whole display.
  const list = Array.isArray(items) ? items : []
  const rows = useRef<(HTMLDivElement | null)[]>([])

  // The line the cashier just rang up glows blue, then settles, and a long
  // order scrolls so it is in view.
  useEffect(() => {
    if (!spotlight) return
    const row = rows.current[spotlight.index]
    if (!row) return
    row.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    play(row, [{ backgroundColor: 'rgba(47,109,181,0.14)' }, { backgroundColor: 'rgba(47,109,181,0)' }], {
      duration: 1600,
      easing: EASE_OUT,
    })
  }, [spotlight])

  if (list.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-cfd-gutter text-center text-cfd-detail text-text-mid">
        {t('no-items-yet')}
      </div>
    )
  }

  return (
    <div className="cart-scroll min-h-0 flex-1 overflow-y-auto px-cfd-1 pb-2">
      {list.map((item, i) => (
        <CartItemRow
          key={`${item.title}-${i}`}
          ref={el => { rows.current[i] = el }}
          item={item}
          currency={currency}
        />
      ))}
    </div>
  )
}
